import { useState } from "react";
import axios from "axios";
import { MagnifyingGlass, CheckCircle, XCircle, Warning, Globe, Storefront, Code, ShieldCheck, Lightning, ArrowClockwise, ShoppingCart, Tag, CaretDown, CaretUp } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  connectivity: { label: "Connectivity", icon: Globe, color: "#32D74B" },
  platform: { label: "Platform", icon: Storefront, color: "#96BF48" },
  widget: { label: "Widget Script", icon: Code, color: "#E85D24" },
  tracking: { label: "Event Tracking", icon: Lightning, color: "#FFD60A" },
  shopify: { label: "Shopify Events", icon: ShoppingCart, color: "#96BF48" },
  wordpress: { label: "WordPress Events", icon: Tag, color: "#21759B" },
  security: { label: "Security", icon: ShieldCheck, color: "#32D74B" },
};

const StatusIcon = ({ passed, severity }) => {
  if (passed === true) return <CheckCircle size={18} weight="fill" className="text-[#32D74B] flex-shrink-0" />;
  if (severity === "warning") return <Warning size={18} weight="fill" className="text-[#FFD60A] flex-shrink-0" />;
  return <XCircle size={18} weight="fill" className="text-[#FF3B30] flex-shrink-0" />;
};

export default function IntegrationTester() {
  const [url, setUrl] = useState("");
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});

  const runTest = async () => {
    if (!url.trim()) return;
    setTesting(true);
    setResults(null);
    try {
      const { data } = await axios.post(`${API}/integration-test`, { url: url.trim() });
      setResults(data);
      // Auto-expand categories with failures
      const expanded = {};
      const cats = [...new Set(data.checks.map(c => c.category))];
      cats.forEach(cat => {
        const hasFail = data.checks.some(c => c.category === cat && !c.passed);
        expanded[cat] = hasFail;
      });
      setExpandedCats(expanded);
    } catch (err) {
      setResults({ error: err.response?.data?.error || "Test failed — check the URL and try again" });
    } finally {
      setTesting(false);
    }
  };

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  // Group checks by category
  const grouped = results?.checks?.reduce((acc, c) => {
    if (!acc[c.category]) acc[c.category] = [];
    acc[c.category].push(c);
    return acc;
  }, {}) || {};

  const score = results?.summary
    ? Math.round((results.summary.passed / (results.summary.passed + results.summary.failed + results.summary.warnings)) * 100)
    : 0;

  const scoreColor = score >= 80 ? "#32D74B" : score >= 50 ? "#FFD60A" : "#FF3B30";

  return (
    <div className="space-y-6" data-testid="integration-tester-tab">

      {/* Input Section */}
      <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6">
        <h2 className="text-lg font-semibold text-white mb-1">Test Your Integration</h2>
        <p className="text-xs text-[#707070] mb-4">
          Paste your website URL below. We'll fetch the page and check for the Arya widget, tracking scripts, and platform-specific events.
        </p>

        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runTest(); }}
              placeholder="https://your-store.myshopify.com or https://your-site.com"
              data-testid="integration-test-url"
              className="w-full pl-10 pr-4 py-3 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg text-white text-sm placeholder-[#555] focus:outline-none focus:border-[#E85D24] transition-colors"
            />
          </div>
          <button
            onClick={runTest}
            disabled={!url.trim() || testing}
            data-testid="integration-test-run"
            className="px-6 py-3 bg-[#E85D24] text-white text-sm font-semibold rounded-lg hover:opacity-85 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {testing ? (
              <><ArrowClockwise size={16} weight="bold" className="animate-spin" /> Testing...</>
            ) : (
              <><MagnifyingGlass size={16} weight="bold" /> Run Test</>
            )}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {testing && (
        <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-8 flex flex-col items-center gap-3">
          <ArrowClockwise size={32} weight="bold" className="text-[#E85D24] animate-spin" />
          <p className="text-sm text-[#888]">Fetching and analyzing page...</p>
          <p className="text-xs text-[#555]">Checking for widget script, tracking events, and platform features</p>
        </div>
      )}

      {/* Error State */}
      {results?.error && (
        <div className="bg-[#121212] border border-[#FF3B30]/40 rounded-sm p-6 flex items-center gap-3">
          <XCircle size={24} weight="fill" className="text-[#FF3B30] flex-shrink-0" />
          <div>
            <p className="text-sm text-white font-medium">Test Failed</p>
            <p className="text-xs text-[#888] mt-0.5">{results.error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {results?.checks && (
        <>
          {/* Score Summary */}
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6" data-testid="integration-test-summary">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-white">Results for {results.url}</h3>
                <p className="text-xs text-[#555] mt-0.5">
                  {results.platform === "shopify" ? "Shopify store" : results.platform === "wordpress" ? "WordPress site" : "Custom site"} &middot; Tested {new Date(results.timestamp).toLocaleTimeString()}
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold" style={{ color: scoreColor }} data-testid="integration-score">
                  {score}%
                </div>
                <p className="text-[10px] text-[#555] mt-0.5">integration score</p>
              </div>
            </div>

            {/* Stats bar */}
            <div className="flex gap-4 pt-3 border-t border-[#1A1A1A]">
              <div className="flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-[#32D74B]" />
                <span className="text-xs text-[#32D74B] font-medium">{results.summary.passed} passed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <XCircle size={14} weight="fill" className="text-[#FF3B30]" />
                <span className="text-xs text-[#FF3B30] font-medium">{results.summary.failed} failed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Warning size={14} weight="fill" className="text-[#FFD60A]" />
                <span className="text-xs text-[#FFD60A] font-medium">{results.summary.warnings} warnings</span>
              </div>
            </div>
          </div>

          {/* Categorized Checks */}
          <div className="space-y-3">
            {Object.entries(grouped).map(([cat, checks]) => {
              const meta = CATEGORY_META[cat] || { label: cat, icon: Code, color: "#888" };
              const Icon = meta.icon;
              const allPassed = checks.every(c => c.passed);
              const hasFail = checks.some(c => !c.passed && c.severity !== "warning");
              const expanded = expandedCats[cat] ?? false;

              return (
                <div key={cat} className="bg-[#121212] border border-[#2A2A2A] rounded-sm overflow-hidden" data-testid={`check-category-${cat}`}>
                  <button
                    onClick={() => toggleCat(cat)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-[#0A0A0A] transition-colors text-left"
                  >
                    <Icon size={18} weight="bold" style={{ color: meta.color }} className="flex-shrink-0" />
                    <span className="text-sm font-medium text-white flex-1">{meta.label}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        allPassed ? "bg-[#32D74B]/15 text-[#32D74B]" :
                        hasFail ? "bg-[#FF3B30]/15 text-[#FF3B30]" :
                        "bg-[#FFD60A]/15 text-[#FFD60A]"
                      }`}>
                        {checks.filter(c => c.passed).length}/{checks.length}
                      </span>
                      {expanded ? <CaretUp size={14} className="text-[#555]" /> : <CaretDown size={14} className="text-[#555]" />}
                    </div>
                  </button>

                  {expanded && (
                    <div className="border-t border-[#1A1A1A]">
                      {checks.map((check) => (
                        <div key={check.name} className="flex items-start gap-3 px-5 py-3 border-b border-[#1A1A1A] last:border-b-0">
                          <StatusIcon passed={check.passed} severity={check.severity} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-white">{check.name}</p>
                            <p className={`text-[11px] mt-0.5 leading-relaxed ${
                              check.passed ? "text-[#888]" : check.severity === "warning" ? "text-[#FFD60A]/80" : "text-[#FF3B30]/80"
                            }`}>
                              {check.detail}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Re-test button */}
          <div className="flex justify-center">
            <button
              onClick={runTest}
              disabled={testing}
              data-testid="integration-retest"
              className="px-5 py-2.5 border border-[#2A2A2A] text-[#888] text-xs font-medium rounded-lg hover:border-[#E85D24] hover:text-[#E85D24] transition-colors disabled:opacity-40"
            >
              <ArrowClockwise size={14} weight="bold" className="inline mr-1.5" />
              Re-test
            </button>
          </div>
        </>
      )}
    </div>
  );
}
